// React
import { useEffect, useState } from "react"

// DateTime
import { DateTime, Duration, useCurrentDate } from "@/lib/dates"

// Utils
import SunCalc from "suncalc"
import { floorTo } from "@/lib/math"

// Constants
import { mapCoordinatesCenterOfNetherlands } from "@/constants/constants"

// UI
import { Tooltip } from "@/components/Tooltip"

// Translations
import { Trans, useTrans } from "@/i18n"

// Context
import { useCurrentUserInfo } from "@/context/user"

export function calculate(diff: Duration): {
	hours: number
	minutes: number
} {
	let hours = Math.floor(diff.hours)
	let minutes = Math.floor(floorTo(diff.minutes, 15))

	return {
		hours,
		minutes,
	}
}

export function dateDiff(from: Date, to: Date): Duration {
	return DateTime.fromJSDate(from).diff(DateTime.fromJSDate(to), [
		"hours",
		"minutes",
	])
}

/**
 * WelcomeText component
 */
export function WelcomeText() {
	const t = useTrans("common")
	const now = useCurrentDate().toJSDate()
	const sunData = SunCalc.getTimes(
		now,
		Number(mapCoordinatesCenterOfNetherlands.lat),
		Number(mapCoordinatesCenterOfNetherlands.lng),
	)

	// State
	const [hours, setHours] = useState<number>(0)
	const [minutes, setMinutes] = useState<number>(0)
	const [messageKey, setMessageKey] = useState<string>("")

	// Queries
	const { first_name } = useCurrentUserInfo()

	// On mount determine text
	useEffect(() => {
		if (now > sunData.sunset) {
			const tomorrowDate = new Date(now)
			tomorrowDate.setDate(now.getDate() + 1)

			const tomorrowSunData = SunCalc.getTimes(
				tomorrowDate,
				Number(mapCoordinatesCenterOfNetherlands.lat),
				Number(mapCoordinatesCenterOfNetherlands.lng),
			)

			const { hours, minutes } = calculate(
				dateDiff(tomorrowSunData.sunrise, now),
			)

			setHours(hours)
			setMinutes(minutes)

			// Set appropriate message key based on hours and minutes
			if (hours === 0) {
				setMessageKey(
					minutes === 1
						? "common.welcome_message.next_sunrise_only_minute_singular"
						: "common.welcome_message.next_sunrise_only_minute_plural",
				)
			} else if (minutes === 0) {
				setMessageKey(
					hours === 1
						? "common.welcome_message.next_sunrise_only_hour_singular"
						: "common.welcome_message.next_sunrise_only_hour_plural",
				)
			} else {
				setMessageKey("common.welcome_message.next_sunrise")
			}
		}

		if (now >= sunData.sunrise) {
			const { hours, minutes } = calculate(dateDiff(now, sunData.sunrise))

			setHours(hours)
			setMinutes(minutes)

			// Set appropriate message key based on hours and minutes
			if (hours === 0) {
				setMessageKey(
					minutes === 1
						? "common.welcome_message.last_sunrise_only_minute_singular"
						: "common.welcome_message.last_sunrise_only_minute_plural",
				)
			} else if (minutes === 0) {
				setMessageKey(
					hours === 1
						? "common.welcome_message.last_sunrise_only_hour_singular"
						: "common.welcome_message.last_sunrise_only_hour_plural",
				)
			} else {
				setMessageKey("common.welcome_message.last_sunrise")
			}
		}

		if (now < sunData.sunrise) {
			const { hours, minutes } = calculate(dateDiff(sunData.sunrise, now))

			setHours(hours)
			setMinutes(minutes)

			// Set appropriate message key based on hours and minutes
			if (hours === 0) {
				setMessageKey(
					minutes === 1
						? "common.welcome_message.next_sunrise_only_minute_singular"
						: "common.welcome_message.next_sunrise_only_minute_plural",
				)
			} else if (minutes === 0) {
				setMessageKey(
					hours === 1
						? "common.welcome_message.next_sunrise_only_hour_singular"
						: "common.welcome_message.next_sunrise_only_hour_plural",
				)
			} else {
				setMessageKey("common.welcome_message.next_sunrise")
			}
		}
	}, [])

	// When we don't have a message key, don't render anything
	if (!messageKey) {
		return null
	}

	return (
		<div className="text-md hidden h-full items-center overflow-hidden text-gray-400 md:flex">
			<Tooltip
				content={t("common:common.layout.welcome.tooltip")}
				className="text-center"
			>
				<p className="truncate">
					<Trans
						i18nKey="common.layout.welcome"
						ns="common"
						values={{ name: first_name }}
						components={{
							strong: <span className="pr-1 text-primary-500" />,
							message: (
								<>
									{t(messageKey, {
										hours,
										minutes,
									})}
								</>
							),
						}}
					/>
				</p>
			</Tooltip>
		</div>
	)
}
